import { useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { CalendarClock, ChevronRight } from "lucide-react";
import { auditClientPath } from "../../nav/auditorNav";
import { frameworkLabel } from "../../nav/frameworkMeta";
import { LoadingBlock, ErrorBlock } from "../../components/Spinner";
import { theme, card, emptyFrame } from "../../theme";
import { fmtDate } from "../../utils/format";
import { AUDIT_TYPE_LABELS, useAuditorClients } from "./auditorShared";
import type { AuditorClientSummary } from "./auditorShared";

// ── Audit calendar ────────────────────────────────────────────────────────
// Every engaged client's scheduled audit (the schedule field of
// GET /auditor/clients), overdue first, then soonest first. Each row links to
// the client's evidence map, where the schedule panel lets the auditor change
// the date.

type ScheduledClient = AuditorClientSummary & { schedule: NonNullable<AuditorClientSummary["schedule"]> };

function daysUntil(iso: string): number {
  return Math.ceil((new Date(iso).getTime() - Date.now()) / 86_400_000);
}

function whenLabel(schedule: ScheduledClient["schedule"]): { text: string; color: string; bg: string } {
  if (schedule.isOverdue) {
    const days = -daysUntil(schedule.scheduledDate);
    return { text: days <= 0 ? "Overdue" : `Overdue by ${days} day${days === 1 ? "" : "s"}`, color: theme.color.dangerText, bg: theme.color.dangerBg };
  }
  const days = daysUntil(schedule.scheduledDate);
  if (days <= 0) return { text: "Today", color: theme.color.warningText, bg: theme.color.warningBg };
  if (days <= 30) return { text: `In ${days} day${days === 1 ? "" : "s"}`, color: theme.color.warningText, bg: theme.color.warningBg };
  return { text: `In ${days} days`, color: theme.color.subtle, bg: theme.color.panelAlt };
}

function CalendarRow({ client, isLast }: { client: ScheduledClient; isLast: boolean }) {
  const when = whenLabel(client.schedule);
  const date = new Date(client.schedule.scheduledDate);
  return (
    <Link
      to={auditClientPath(client.id, "evidence-map")}
      style={{
        display: "flex", alignItems: "center", gap: 14, padding: "12px 18px",
        borderBottom: isLast ? "none" : "1px solid var(--c-border)",
        textDecoration: "none", color: "inherit", transition: "background 0.12s",
      }}
      onMouseEnter={(e) => { e.currentTarget.style.background = "var(--c-panelAlt)"; }}
      onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
    >
      <div
        aria-hidden="true"
        style={{
          width: 46, flexShrink: 0, textAlign: "center", borderRadius: 3, padding: "5px 0",
          background: client.schedule.isOverdue ? theme.color.dangerBg : theme.color.panelAlt,
          border: `1px solid ${client.schedule.isOverdue ? theme.color.danger : theme.color.border}`,
        }}
      >
        <div style={{ fontSize: 15, fontWeight: 800, lineHeight: 1.1, color: client.schedule.isOverdue ? theme.color.dangerText : theme.color.heading }}>
          {date.getDate()}
        </div>
        <div style={{ fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: theme.color.faint }}>
          {date.toLocaleDateString("en-GB", { month: "short" })}
        </div>
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13.5, fontWeight: 600, color: theme.color.heading, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {client.name}
        </div>
        <div style={{ fontSize: 11.5, color: theme.color.subtle, marginTop: 2, display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
          <span>{AUDIT_TYPE_LABELS[client.schedule.auditType] ?? client.schedule.auditType}</span>
          <span aria-hidden="true" style={{ color: theme.color.faint }}>-</span>
          <span>{fmtDate(client.schedule.scheduledDate)}</span>
          {client.frameworkIds.length > 0 && (
            <>
              <span aria-hidden="true" style={{ color: theme.color.faint }}>-</span>
              <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {client.frameworkIds.map(frameworkLabel).join(", ")}
              </span>
            </>
          )}
        </div>
      </div>
      <span style={{ fontSize: 11, fontWeight: 700, padding: "2px 9px", borderRadius: 3, flexShrink: 0, color: when.color, background: when.bg }}>
        {when.text}
      </span>
      <ChevronRight size={14} style={{ color: "var(--c-faint)", flexShrink: 0 }} />
    </Link>
  );
}

export function AuditorCalendarPage() {
  const { clients, loading, error, reload } = useAuditorClients();

  // The shell loads the list once; a visit here picks up schedule changes made since.
  useEffect(() => { reload(); }, [reload]);

  const scheduled = useMemo<ScheduledClient[]>(() => {
    const rows = clients.filter((c): c is ScheduledClient => c.schedule !== null);
    return rows.sort((a, b) => {
      if (a.schedule.isOverdue !== b.schedule.isOverdue) return a.schedule.isOverdue ? -1 : 1;
      return new Date(a.schedule.scheduledDate).getTime() - new Date(b.schedule.scheduledDate).getTime();
    });
  }, [clients]);

  const overdue = scheduled.filter((c) => c.schedule.isOverdue);
  const upcoming = scheduled.filter((c) => !c.schedule.isOverdue);
  const unscheduled = clients.length - scheduled.length;

  if (loading && clients.length === 0) return <LoadingBlock label="Loading calendar" />;
  if (error && clients.length === 0) return <ErrorBlock message={error} onRetry={reload} />;

  return (
    <div className="page-pad">
      <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: theme.color.heading }}>Calendar</h2>
          <p style={{ margin: "4px 0 0", fontSize: 13, color: theme.color.subtle }}>
            Scheduled audits across your engaged clients, overdue first. Open a client to change its date.
          </p>
        </div>

        {scheduled.length === 0 ? (
          <div style={{ ...emptyFrame, padding: "40px 24px" }}>
            <CalendarClock size={28} style={{ color: "var(--c-faint)", marginBottom: 10 }} />
            <div style={{ fontSize: 15, fontWeight: 700, color: theme.color.heading, marginBottom: 4 }}>Nothing scheduled</div>
            <div style={{ fontSize: 13, color: theme.color.subtle }}>
              {clients.length === 0
                ? "No clients have engaged your bureau yet."
                : "Set an audit date from the schedule panel on a client's evidence map."}
            </div>
          </div>
        ) : (
          <>
            {overdue.length > 0 && (
              <section aria-labelledby="calendar-overdue">
                <div id="calendar-overdue" style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: theme.color.dangerText, marginBottom: 8 }}>
                  Overdue ({overdue.length})
                </div>
                <div style={{ ...card, padding: 0, overflow: "hidden", borderColor: theme.color.danger }}>
                  {overdue.map((c, i) => <CalendarRow key={c.id} client={c} isLast={i === overdue.length - 1} />)}
                </div>
              </section>
            )}
            {upcoming.length > 0 && (
              <section aria-labelledby="calendar-upcoming">
                <div id="calendar-upcoming" style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: theme.color.faint, marginBottom: 8 }}>
                  Upcoming ({upcoming.length})
                </div>
                <div style={{ ...card, padding: 0, overflow: "hidden" }}>
                  {upcoming.map((c, i) => <CalendarRow key={c.id} client={c} isLast={i === upcoming.length - 1} />)}
                </div>
              </section>
            )}
          </>
        )}

        {unscheduled > 0 && scheduled.length > 0 && (
          <div style={{ fontSize: 12, color: theme.color.faint }}>
            {unscheduled} client{unscheduled === 1 ? " has" : "s have"} no audit scheduled.
          </div>
        )}
      </div>
    </div>
  );
}
